import {
  FileDownloadOutlined,
  LocalPrintshopOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Card, Chip, Divider, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { upperCase } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { ImageHandler } from "../../../shared/components/ImageHandler";
import { ETheme } from "../../../shared/enums/user/ETheme";
import { formatDate } from "../../../shared/functions/formatDate";
import { formatPrice } from "../../../shared/functions/formatPrice";
import { getInvoiceStatusColor } from "../../../shared/functions/getChipColors";
import { handleError } from "../../../shared/functions/handleError";
import {
  IInvoice,
  IInvoiceItem,
} from "../../../shared/interfaces/invoice/IInvoice";
import { IUser } from "../../../shared/interfaces/user/IUser";
import { darkPalette, lightPalette } from "../../../shared/mui/palettes";
import { RootState } from "../../../store/store";
import { downloadFile } from "../../../utils/api/api.service";
import { INVOICE_URL } from "../../../utils/api/endpoints";
import { AdminInvoiceService } from "../service";
import InvoiceFooterCalculation from "./InvoiceFooterCalculation";

export const footerInvoiceKeys = ["itemsSum", "taxes", "discount"];

const InvoicePreview = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const printRef = useRef<HTMLDivElement>(null);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [invoice, setInvoice] = useState<null | IInvoice>(null);
  const { theme } = useSelector(
    (state: RootState) => state.auth.userData,
  ) as IUser;
  const company = useSelector((state: RootState) => state?.companySettings);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });
  const handleDownload = async () => {
    setIsDownloadLoading(true);
    console.log(1);
    await downloadFile(
      `${INVOICE_URL}/download/${id}`,
      "pdf",
      `Invoice - ${id}`,
    )
      .then((response) => {
        console.log(response);
      })
      .catch(handleError)
      .finally(() => setIsDownloadLoading(false));
  };
  useEffect(() => {
    if (id) {
      AdminInvoiceService.getSingle(Number(id))
        .then(setInvoice)
        .catch(handleError);
    }
  }, [id]);
  return (
    <Card>
      {invoice?.id && (
        <>
          <Box
            sx={{
              marginBottom: "20px",
              borderBottom: `1px solid ${theme === ETheme.LIGHT ? lightPalette.primary.light : darkPalette.background.light}`,
              paddingBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" fontWeight={"bold"}>
              {t("invoice.invoicePreview")}
            </Typography>
            <Box>
              <Button
                variant={"contained"}
                color={"warning"}
                sx={{ mr: "10px" }}
                onClick={() => invoice && handlePrint()}
              >
                <LocalPrintshopOutlined sx={{ pr: "5px" }} />
                {t("button.print")}
              </Button>
              <LoadingButton
                loading={isDownloadLoading}
                variant={"contained"}
                onClick={() => invoice && handleDownload()}
              >
                <FileDownloadOutlined sx={{ pr: "5px" }} />
                {t("button.download")}
              </LoadingButton>
            </Box>
          </Box>
          <Card>
            <Grid container spacing={2} ref={printRef}>
              <Grid size={{ md: 10 }} sx={{ mb: "30px" }}>
                <ImageHandler
                  src={
                    theme === ETheme.DARK
                      ? company?.invertedThumbnail
                      : company?.path
                  }
                  className={"invoice-company-logo"}
                  alt={`${company?.name} Logo`}
                />
              </Grid>
              <Grid size={{ md: 2 }} sx={{ mb: "30px" }}>
                <Chip
                  label={t(`invoice.status.${invoice.status}`)}
                  color={getInvoiceStatusColor(invoice.status)}
                  variant="outlined"
                />
                <Box sx={{ mt: "10px" }}>
                  <Typography sx={{ fontWeight: "600" }}>
                    INV - #{invoice?.invoiceNumber}
                  </Typography>
                  <Typography
                    variant={"subtitle2"}
                    color={lightPalette.text.gray}
                    sx={{
                      fontWeight: "300",
                    }}
                  >
                    {t("invoice.invoiceNumber")}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ md: 5 }}>
                <Typography sx={{ fontWeight: "600" }}>
                  {t("invoice.invoiceFrom")}:
                </Typography>
                <Typography
                  variant={"h6"}
                  sx={{
                    fontWeight: "600",
                  }}
                >
                  {upperCase(company?.title)}
                </Typography>
                <Typography
                  variant={"subtitle2"}
                  color={lightPalette.text.gray}
                  sx={{
                    fontWeight: "300",
                  }}
                >
                  {t(`country.${company?.country}`)}
                  <br />
                  {company?.address},
                  <br />
                  {company?.city}
                  <br />
                  {company?.phoneNumber}
                </Typography>
              </Grid>
              <Grid size={{ md: 5 }}>
                <Typography sx={{ fontWeight: "600" }}>
                  {t("invoice.billTo")}:
                </Typography>
                <Typography
                  variant={"h6"}
                  sx={{
                    fontWeight: "600",
                  }}
                >
                  {upperCase(invoice?.client?.name)}
                </Typography>
                <Typography
                  variant={"subtitle2"}
                  color={lightPalette.text.gray}
                  sx={{
                    fontWeight: "300",
                  }}
                >
                  {invoice?.client?.email ?? ""}
                  <br />
                </Typography>
              </Grid>
              <Grid size={{ md: 2 }}>
                <img src={invoice?.qrLink} alt="QR Code of invoice" />
              </Grid>
              <Grid size={{ md: 5 }}>
                <Typography sx={{ fontWeight: "600" }}>
                  {t("invoice.creationDate")}:
                </Typography>
                <Typography
                  variant={"subtitle2"}
                  color={lightPalette.text.gray}
                  sx={{
                    fontWeight: "300",
                  }}
                >
                  {formatDate(invoice?.createdAt as string, "MMM DD, YYYY")}
                </Typography>
              </Grid>
              <Grid size={{ md: 5 }}>
                <Typography sx={{ fontWeight: "600" }}>
                  {t("invoice.dueDate")}:
                </Typography>
                <Typography
                  variant={"subtitle2"}
                  color={lightPalette.text.gray}
                  sx={{
                    fontWeight: "300",
                  }}
                >
                  {formatDate(invoice?.dueDate as string, "MMM DD, YYYY")}
                </Typography>
              </Grid>
              <Grid size={{ md: 12 }} />

              <Grid
                container
                sx={{
                  width: "100%",
                  py: "10px",
                  borderRadius: "10px",
                  backgroundColor: lightPalette.background.paper,
                }}
              >
                <Grid size={{ md: 1 }}>
                  <Typography
                    variant={"subtitle2"}
                    color={lightPalette.text.gray}
                    sx={{
                      textAlign: "center",
                      fontWeight: "500",
                    }}
                  >
                    #
                  </Typography>
                </Grid>
                <Grid size={{ md: 4 }}>
                  <Typography
                    variant={"subtitle2"}
                    color={lightPalette.text.gray}
                    sx={{
                      fontWeight: "500",
                    }}
                  >
                    {upperCase(t("invoice.item"))}
                  </Typography>
                </Grid>
                <Grid size={{ md: 2 }}>
                  <Typography
                    variant={"subtitle2"}
                    color={lightPalette.text.gray}
                    sx={{
                      fontWeight: "500",
                    }}
                  >
                    {upperCase(t("invoice.quantity"))}
                  </Typography>
                </Grid>
                <Grid size={{ md: 2 }}>
                  <Typography
                    variant={"subtitle2"}
                    color={lightPalette.text.gray}
                    sx={{
                      fontWeight: "500",
                    }}
                  >
                    {upperCase(t("invoice.unitPrice"))}
                  </Typography>
                </Grid>
                <Grid size={{ md: 2 }}>
                  <Typography
                    variant={"subtitle2"}
                    color={lightPalette.text.gray}
                    sx={{
                      fontWeight: "500",
                    }}
                  >
                    {upperCase(t("invoice.total"))}
                  </Typography>
                </Grid>
              </Grid>
              {invoice?.items?.map((invoiceItem) => {
                return (
                  <Grid
                    key={invoiceItem?.id}
                    container
                    sx={{
                      width: "100%",
                    }}
                  >
                    <Grid size={{ md: 1 }}>
                      <Typography
                        variant={"subtitle2"}
                        color={lightPalette.text.gray}
                        sx={{
                          textAlign: "center",
                          fontWeight: "500",
                        }}
                      >
                        {invoiceItem?.id}
                      </Typography>
                    </Grid>
                    <Grid size={{ md: 4 }}>
                      <Typography variant={"subtitle2"}>
                        {invoiceItem?.name}
                      </Typography>
                      <Typography
                        variant={"subtitle2"}
                        color={lightPalette.text.gray}
                        sx={{ fontWeight: "300" }}
                      >
                        {invoiceItem?.description}
                      </Typography>
                    </Grid>
                    <Grid size={{ md: 2 }}>
                      <Typography
                        color={lightPalette.text.gray}
                        variant={"subtitle2"}
                        sx={{ fontWeight: "300" }}
                      >
                        {invoiceItem?.quantity}
                      </Typography>
                    </Grid>
                    <Grid size={{ md: 2 }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: "300" }}
                        color={lightPalette.text.gray}
                      >
                        {formatPrice(invoiceItem?.price as number)}
                      </Typography>
                    </Grid>
                    <Grid size={{ md: 2 }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: "300" }}
                        color={lightPalette.text.gray}
                      >
                        {formatPrice(invoiceItem?.itemSum as number)}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
            <Divider sx={{ mb: "20px", mt: "10px" }} />
            <Grid container>
              <Grid size={{ md: 8 }}>
                <Typography sx={{ fontWeight: "600" }}>
                  {t("invoice.notes")}:
                </Typography>
                <Typography
                  variant={"subtitle2"}
                  color={lightPalette.text.gray}
                  sx={{
                    fontWeight: "300",
                  }}
                >
                  We appreciate your business. Should you need us to add VAT or
                  extra notes let us know!
                </Typography>
              </Grid>
              <Grid size={{ md: 4 }}>
                <InvoiceFooterCalculation
                  items={invoice?.items as IInvoiceItem[]}
                  discount={Number(invoice?.discount)}
                  taxes={Number(invoice?.taxes)}
                />
              </Grid>
            </Grid>
          </Card>
        </>
      )}
    </Card>
  );
};

export default InvoicePreview;
